import { BaseModel } from "../Generics/BaseModel"
import { OperatingScheduleEntity } from "../OperatingSchedule/OperatingScheduleEntity"
import { AddressEntity } from "../Person/AddressEntity"
import { StorePaymentSettingsEntity } from "./StorePaymentSettingsEntity"
import { SubscriptionEntity as SubscriptionEntity } from "./SubscriptionEntity"
import { EStoreServiceType } from "../../Enum/EStoreServiceType"
import { EDeliveryFulfillmentType } from "../../Enum/EDeliveryFulfillmentType"
import { EFulfillmentMode } from "../../Enum/EFulfillmentMode"
import { EZeroValueSelectionOptionDisplayMode } from "../../Enum/EZeroValueSelectionOptionDisplayMode"
import { CompanySocialMediaEntity } from "./CompanySocialMediaEntity"
import { getDateKeyInTimeZone } from "../../utils/timeZone"

export class CompanyEntity extends BaseModel {
  id: number

  companyName: string

  socialReason: string

  cnpj: string

  stateRegistration: string

  companyMessage: string

  iconUrl: string

  slug: string

  timeZoneId: string

  companySocialMediaEntity: CompanySocialMediaEntity

  storeServiceType: EStoreServiceType

  localServiceRadiusMeters: number

  isFreeShippingEnabled: boolean

  freeShippingMinimumOrderValue: number

  isFixedShippingFeeEnabled: boolean

  fixedShippingFeeValue: number

  isDeliveryOrderNotificationEnabled: boolean

  isTestOrDemoCompany: boolean

  deliveryFulfillmentType: EDeliveryFulfillmentType

  fulfillmentMode: EFulfillmentMode

  zeroValueSelectionOptionDisplayMode: EZeroValueSelectionOptionDisplayMode

  isServicePointSelfServiceEnabled: boolean

  isServicePointSelfServiceOrderingEnabled: boolean

  defaultServicePointId: number | null

  publicMenuCardLayout: string

  publicMenuPrimaryColor: string

  publicMenuSecondaryColor: string

  publicMenuAccentColor: string

  publicMenuBackgroundColor: string

  publicMenuSurfaceColor: string

  publicMenuTextColor: string

  publicMenuBorderColor: string

  publicMenuButtonColor: string

  publicMenuButtonTextColor: string

  publicMenuSearchBackgroundColor: string

  publicMenuSearchBorderColor: string

  publicMenuMobileNavBackgroundColor: string

  publicMenuMobileNavActiveBackgroundColor: string

  publicMenuMobileNavActiveTextColor: string

  publicMenuMobileNavActiveIconColor: string

  publicMenuMobileNavInactiveBackgroundColor: string

  publicMenuMobileNavInactiveTextColor: string

  publicMenuMobileNavInactiveIconColor: string

  publicMenuHeaderBackgroundColor: string

  publicMenuRestaurantNameColor: string

  publicMenuRestaurantDescriptionColor: string

  publicMenuStatusCardBackgroundColor: string

  publicMenuCartButtonColor: string

  publicMenuCartBackgroundColor: string

  publicMenuCartBorderColor: string

  publicMenuCartTitleTextColor: string

  publicMenuCartDescriptionTextColor: string

  publicMenuCartTotalLabelTextColor: string

  publicMenuCartTotalValueTextColor: string

  publicMenuEmptyStateTitleColor: string

  publicMenuEmptyStateDescriptionColor: string

  publicMenuCategoryBackgroundColor: string

  publicMenuCategoryBorderColor: string

  publicMenuCategoryDividerColor: string

  publicMenuLoginButtonBackgroundColor: string

  publicMenuLoginButtonTextColor: string

  publicMenuShareButtonBackgroundColor: string

  publicMenuShareButtonTextColor: string

  publicMenuServiceSelectorBackgroundColor: string

  publicMenuServiceSelectorTextColor: string

  publicMenuServiceSelectorActiveBackgroundColor: string

  publicMenuServiceSelectorActiveTextColor: string

  publicMenuBorderThickness: number

  publicMenuHighlightPromotions: boolean

  publicMenuCompactCategories: boolean

  publicMenuUseGradientHeader: boolean

  publicMenuShowAwardsTab: boolean

  publicMenuShowPromotionsTab: boolean

  publicMenuShowExtraServicesTab: boolean

  publicMenuTrackingEnabled: boolean

  publicMenuGoogleTagManagerId: string

  publicMenuGoogleAnalyticsMeasurementId: string

  publicMenuGoogleAdsConversionId: string

  publicMenuMetaPixelId: string

  isVacationEnabled: boolean

  vacationStartDate: string

  vacationEndDate: string

  addressEntity: AddressEntity | null

  operatingSchedules: OperatingScheduleEntity[]

  storePaymentSettingsEntity: StorePaymentSettingsEntity

  subscriptionEntity: SubscriptionEntity

  canCreatePublicOrder: boolean

  orderPaymentTimeoutMinutes: number

  unpaidOrderRetentionDays: number

  platformFeePercent: number

  constructor(data: Partial<CompanyEntity> = {}) {
    super()

    this.setUpdateData(data)
  }

  setUpdateData(data: Partial<CompanyEntity>) {
    this.slug = data.slug ?? ""

    this.timeZoneId = data.timeZoneId ?? "America/Sao_Paulo"

    this.iconUrl = data.iconUrl ?? ""

    this.id = data.id ?? -1

    this.companyName = data.companyName ?? ""

    this.socialReason = data.socialReason ?? ""

    this.cnpj = data.cnpj ?? ""

    this.stateRegistration = data.stateRegistration ?? ""

    this.companyMessage = data.companyMessage ?? ""

    this.companySocialMediaEntity = data.companySocialMediaEntity
      ? new CompanySocialMediaEntity(data.companySocialMediaEntity)
      : new CompanySocialMediaEntity()

    this.storeServiceType = data.storeServiceType ?? EStoreServiceType.Both

    this.localServiceRadiusMeters = data.localServiceRadiusMeters ?? 100

    this.isFreeShippingEnabled = data.isFreeShippingEnabled ?? false

    this.freeShippingMinimumOrderValue = data.freeShippingMinimumOrderValue ?? 0

    this.isFixedShippingFeeEnabled = data.isFixedShippingFeeEnabled ?? false

    this.fixedShippingFeeValue = data.fixedShippingFeeValue ?? 0

    this.isDeliveryOrderNotificationEnabled = data.isDeliveryOrderNotificationEnabled ?? true

    this.isTestOrDemoCompany = data.isTestOrDemoCompany ?? false

    this.deliveryFulfillmentType = data.deliveryFulfillmentType ?? EDeliveryFulfillmentType.Both

    this.fulfillmentMode = data.fulfillmentMode ?? EFulfillmentMode.Combined

    this.zeroValueSelectionOptionDisplayMode =
      data.zeroValueSelectionOptionDisplayMode ?? EZeroValueSelectionOptionDisplayMode.ShowFree

    this.isServicePointSelfServiceEnabled = data.isServicePointSelfServiceEnabled ?? false

    this.isServicePointSelfServiceOrderingEnabled = data.isServicePointSelfServiceOrderingEnabled ?? false

    this.defaultServicePointId = data.defaultServicePointId ?? null

    this.publicMenuCardLayout = data.publicMenuCardLayout ?? "cover"

    this.publicMenuPrimaryColor = data.publicMenuPrimaryColor ?? "#f97316"

    this.publicMenuSecondaryColor = data.publicMenuSecondaryColor ?? "#fff7ed"

    this.publicMenuAccentColor = data.publicMenuAccentColor ?? "#10b981"

    this.publicMenuBackgroundColor = data.publicMenuBackgroundColor ?? "#ffffff"

    this.publicMenuSurfaceColor = data.publicMenuSurfaceColor ?? "#ffffff"

    this.publicMenuTextColor = data.publicMenuTextColor ?? "#111827"

    this.publicMenuBorderColor = data.publicMenuBorderColor ?? "#F3F4F6"

    this.publicMenuButtonColor = data.publicMenuButtonColor ?? "#10B981"

    this.publicMenuButtonTextColor = data.publicMenuButtonTextColor ?? "#FFFFFF"

    this.publicMenuSearchBackgroundColor = data.publicMenuSearchBackgroundColor ?? "#FFFFFF"

    this.publicMenuSearchBorderColor = data.publicMenuSearchBorderColor ?? "#E5E7EB"

    this.publicMenuMobileNavBackgroundColor = data.publicMenuMobileNavBackgroundColor ?? "#FFFFFF"

    this.publicMenuMobileNavActiveBackgroundColor = data.publicMenuMobileNavActiveBackgroundColor ?? "#FFEDD5"

    this.publicMenuMobileNavActiveTextColor = data.publicMenuMobileNavActiveTextColor ?? "#EA580C"

    this.publicMenuMobileNavActiveIconColor = data.publicMenuMobileNavActiveIconColor ?? "#C2410C"

    this.publicMenuMobileNavInactiveBackgroundColor = data.publicMenuMobileNavInactiveBackgroundColor ?? "#FFFFFF"

    this.publicMenuMobileNavInactiveTextColor = data.publicMenuMobileNavInactiveTextColor ?? "#9CA3AF"

    this.publicMenuMobileNavInactiveIconColor = data.publicMenuMobileNavInactiveIconColor ?? "#6B7280"

    this.publicMenuHeaderBackgroundColor = data.publicMenuHeaderBackgroundColor ?? "#FFFFFF"

    this.publicMenuRestaurantNameColor = data.publicMenuRestaurantNameColor ?? "#030712"

    this.publicMenuRestaurantDescriptionColor = data.publicMenuRestaurantDescriptionColor ?? "#6B7280"

    this.publicMenuStatusCardBackgroundColor = data.publicMenuStatusCardBackgroundColor ?? "#ECFDF5"

    this.publicMenuCartButtonColor = data.publicMenuCartButtonColor ?? "#EA580C"

    this.publicMenuCartBackgroundColor = data.publicMenuCartBackgroundColor ?? "#FFFFFF"

    this.publicMenuCartBorderColor = data.publicMenuCartBorderColor ?? "#FED7AA"

    this.publicMenuCartTitleTextColor = data.publicMenuCartTitleTextColor ?? "#F97316"

    this.publicMenuCartDescriptionTextColor = data.publicMenuCartDescriptionTextColor ?? "#6B7280"

    this.publicMenuCartTotalLabelTextColor = data.publicMenuCartTotalLabelTextColor ?? "#9CA3AF"

    this.publicMenuCartTotalValueTextColor = data.publicMenuCartTotalValueTextColor ?? "#111827"

    this.publicMenuEmptyStateTitleColor = data.publicMenuEmptyStateTitleColor ?? "#111827"

    this.publicMenuEmptyStateDescriptionColor = data.publicMenuEmptyStateDescriptionColor ?? "#6B7280"

    this.publicMenuCategoryBackgroundColor = data.publicMenuCategoryBackgroundColor ?? "#FFFFFF"

    this.publicMenuCategoryBorderColor = data.publicMenuCategoryBorderColor ?? "#F3F4F6"

    this.publicMenuCategoryDividerColor = data.publicMenuCategoryDividerColor ?? "#F3F4F6"

    this.publicMenuLoginButtonBackgroundColor = data.publicMenuLoginButtonBackgroundColor ?? "#FFFFFF"

    this.publicMenuLoginButtonTextColor = data.publicMenuLoginButtonTextColor ?? "#111827"

    this.publicMenuShareButtonBackgroundColor = data.publicMenuShareButtonBackgroundColor ?? "#FFF7ED"

    this.publicMenuShareButtonTextColor = data.publicMenuShareButtonTextColor ?? "#15803D"

    this.publicMenuServiceSelectorBackgroundColor = data.publicMenuServiceSelectorBackgroundColor ?? "#F3F4F6"

    this.publicMenuServiceSelectorTextColor = data.publicMenuServiceSelectorTextColor ?? "#111827"

    this.publicMenuServiceSelectorActiveBackgroundColor =
      data.publicMenuServiceSelectorActiveBackgroundColor ?? "#F97316"

    this.publicMenuServiceSelectorActiveTextColor = data.publicMenuServiceSelectorActiveTextColor ?? "#111827"

    this.publicMenuBorderThickness = data.publicMenuBorderThickness ?? 1

    this.publicMenuHighlightPromotions = data.publicMenuHighlightPromotions ?? true

    this.publicMenuCompactCategories = data.publicMenuCompactCategories ?? false

    this.publicMenuUseGradientHeader = data.publicMenuUseGradientHeader ?? true

    this.publicMenuShowAwardsTab = data.publicMenuShowAwardsTab ?? true

    this.publicMenuShowPromotionsTab = data.publicMenuShowPromotionsTab ?? true

    this.publicMenuShowExtraServicesTab = data.publicMenuShowExtraServicesTab ?? true

    this.publicMenuTrackingEnabled = data.publicMenuTrackingEnabled ?? false

    this.publicMenuGoogleTagManagerId = data.publicMenuGoogleTagManagerId ?? ""

    this.publicMenuGoogleAnalyticsMeasurementId = data.publicMenuGoogleAnalyticsMeasurementId ?? ""

    this.publicMenuGoogleAdsConversionId = data.publicMenuGoogleAdsConversionId ?? ""

    this.publicMenuMetaPixelId = data.publicMenuMetaPixelId ?? ""

    this.isVacationEnabled = data.isVacationEnabled ?? false

    this.vacationStartDate = data.vacationStartDate ?? ""

    this.vacationEndDate = data.vacationEndDate ?? ""

    this.addressEntity = data.addressEntity ? new AddressEntity(data.addressEntity) : null

    this.storePaymentSettingsEntity = data.storePaymentSettingsEntity
      ? new StorePaymentSettingsEntity(data.storePaymentSettingsEntity)
      : null

    this.subscriptionEntity = data.subscriptionEntity
      ? new SubscriptionEntity(data.subscriptionEntity)
      : new SubscriptionEntity({})

    this.canCreatePublicOrder = data.canCreatePublicOrder ?? true

    this.orderPaymentTimeoutMinutes = data.orderPaymentTimeoutMinutes ?? 10

    this.unpaidOrderRetentionDays = data.unpaidOrderRetentionDays ?? 7

    this.platformFeePercent = data.platformFeePercent ?? 0

    this.operatingSchedules = (data.operatingSchedules ?? []).map(
      (item: OperatingScheduleEntity) => new OperatingScheduleEntity(item),
    )
  }

  isOnVacation(date: Date = new Date()): boolean {
    if (!this.isVacationEnabled || !this.vacationStartDate || !this.vacationEndDate) {
      return false
    }

    const target = getDateKeyInTimeZone(date, this.timeZoneId)

    return target >= this.vacationStartDate.slice(0, 10) && target <= this.vacationEndDate.slice(0, 10)
  }
}
